import React, { useMemo, useState } from 'react';
import type { TableColumnsType } from 'antd';
import { Button, message as messageApi, Table } from 'antd';
import FilterArea, {
  DEFAULT_HANDOVER_CARRIER_FILTER_OPTIONS,
  type HandoverCarrierFilterOptions,
} from './components/FilterArea';
import HandoverCarrierFormDrawer from './components/HandoverCarrierFormDrawer';
import {
  useHandoverCarriers,
  type HandoverCarrierFiltersInput,
} from './graphql/queries/getHandoverCarriers';
import { useSaveHandoverCarrier } from './graphql/mutations/saveHandoverCarrier';
import { useWarehouseOptions } from '../../../hooks/useWarehouseOptions';
import { showError } from 'utils/exceptionUtils';
import { parseResultCode } from '../../../utils/resultCode';
import Loading from '../../../components/Loading';
import { useTranslation } from 'react-i18next';
import { HandoverCarrierType } from '../../../graphql/types/HandoverCarrierType';
import { formatTimestampToDateTime } from '../../../utils/dateTimeUtils';
import { ALL_CENTER_WAREHOUSE_ID } from '../../../enums/TmsEnum';
import { SiteType } from '../../../graphql/types/SiteType';
import TablePaginationBar from '../../../components/TablePaginationBar';

const DEFAULT_PAGE_SIZE = 10;

const PageBody: React.FC = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState<HandoverCarrierType | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');

  const [tempFilters, setTempFilters] = useState<HandoverCarrierFilterOptions>(
    DEFAULT_HANDOVER_CARRIER_FILTER_OPTIONS
  );
  const [filters, setFilters] = useState<HandoverCarrierFilterOptions>(
    DEFAULT_HANDOVER_CARRIER_FILTER_OPTIONS
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { t } = useTranslation();

  const queryFilters: HandoverCarrierFiltersInput = useMemo(
    () => ({
      carrierName: filters.carrierName || null,
      contactName: filters.contactName || null,
      status: filters.status,
      carrierType: filters.carrierType,
    }),
    [filters]
  );

  const { pageResponse, loading, refetch } = useHandoverCarriers(queryFilters, {
    page: currentPage,
    size: pageSize,
  });

  const { warehouseOptions, loading: warehousesLoading } = useWarehouseOptions(SiteType.HUB);

  const extendedWarehouseOptions = useMemo(() => {
    const nationwide = { id: ALL_CENTER_WAREHOUSE_ID, name: t('common.allCenterWarehouse') };
    const exists = warehouseOptions.some((w) => String(w.id) === ALL_CENTER_WAREHOUSE_ID);
    return exists ? warehouseOptions : [nationwide, ...warehouseOptions];
  }, [warehouseOptions, t]);

  const [saveHandoverCarrier, { loading: saving }] = useSaveHandoverCarrier();

  const handleFilter = () => {
    setFilters(tempFilters);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setTempFilters(DEFAULT_HANDOVER_CARRIER_FILTER_OPTIONS);
    setFilters(DEFAULT_HANDOVER_CARRIER_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const showNewDrawer = () => {
    setSelectedCarrier(null);
    setDrawerMode('create');
    setDrawerVisible(true);
  };

  const showEditDrawer = (carrier: HandoverCarrierType) => {
    setSelectedCarrier(carrier);
    setDrawerMode('edit');
    setDrawerVisible(true);
  };

  const handleSubmit = async (values: HandoverCarrierType) => {
    try {
      const input = {
        id: values.id || undefined,
        carrierName: values.carrierName,
        shortCarrierName: values.shortCarrierName ?? undefined,
        carrierType: values.carrierType,
        contactName: values.contactName ?? undefined,
        contactPhone: values.contactPhone ?? undefined,
        centerWarehouseIds: values.centerWarehouseIds ?? [],
        status: values.status,
      };
      const { data } = await saveHandoverCarrier({ variables: { input } });
      const result = data?.saveHandoverCarrier;
      if (!result?.success) {
        messageApi.error(
          parseResultCode(result?.code ?? '', result?.message) ||
            t('central.handoverCarrier.messages.saveFailed') as string
        );
        return;
      }
      messageApi.success(
        drawerMode === 'create'
          ? (t('central.handoverCarrier.messages.createdSuccessfully') as string)
          : (t('central.handoverCarrier.messages.updatedSuccessfully') as string)
      );
      setDrawerVisible(false);
      refetch();
    } catch (err: any) {
      showError(err);
    }
  };

  const columns: TableColumnsType<HandoverCarrierType> = [
    {
      title: t('central.handoverCarrier.pageDetails.id'),
      dataIndex: 'id',
      key: 'id',
      align: 'left',
      render: (text, record) => {
        const display = text ? `H${String(text).padStart(5, '0')}` : '-';
        return (
          <Button type="link" onClick={() => showEditDrawer(record)} style={{ paddingLeft: 0 }}>
            <span style={{ whiteSpace: 'nowrap' }}>{display}</span>
          </Button>
        );
      },
    },
    {
      title: t('central.handoverCarrier.pageDetails.carrierName'),
      dataIndex: 'carrierName',
      key: 'carrierName',
      align: 'left',
      width: 200,
      ellipsis: false,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>,
    },
    {
      title: t('central.handoverCarrier.pageDetails.shortCarrierName'),
      dataIndex: 'shortCarrierName',
      key: 'shortCarrierName',
      align: 'left',
      width: 120,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>,
    },
    {
      title: t('central.handoverCarrier.pageDetails.contactName'),
      dataIndex: 'contactName',
      key: 'contactName',
      align: 'left',
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>,
    },
    {
      title: t('central.handoverCarrier.pageDetails.contactPhone'),
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      align: 'left',
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>,
    },
    {
      title: t('central.handoverCarrier.pageDetails.carrierType'),
      dataIndex: 'carrierType',
      key: 'carrierType',
      align: 'left',
      render: (value: number) => {
        const text =
          value === 1
            ? t('central.handoverCarrier.types.localLastMile')
            : value === 2
            ? t('central.handoverCarrier.types.linehaul')
            : '-';
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      },
    },
    {
      title: t('central.handoverCarrier.pageDetails.warehouse'),
      dataIndex: 'centerWarehouseIds',
      key: 'centerWarehouseIds',
      align: 'left',
      render: (centerWarehouseIds: string[]) => {
        const ids = Array.isArray(centerWarehouseIds) ? centerWarehouseIds : [];
        if (ids.length === 0) return <span style={{ whiteSpace: 'nowrap' }}>-</span>;
        const names = ids
          .map((id) => extendedWarehouseOptions.find((w) => String(w.id) === String(id))?.name)
          .filter(Boolean);
        return (
          <span style={{ whiteSpace: 'nowrap' }}>{names.length > 0 ? names.join(', ') : '-'}</span>
        );
      },
    },
    {
      title: t('central.handoverCarrier.pageDetails.status'),
      dataIndex: 'status',
      key: 'status',
      align: 'left',
      render: (value: number) => {
        const text =
          value === 1
            ? t('enum.activeStatus.active')
            : value === 0
            ? t('enum.activeStatus.suspended')
            : '-';
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      },
    },
    {
      title: t('central.handoverCarrier.pageDetails.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      align: 'left',
      render: (updatedAt: number) => (
        <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>
          {updatedAt ? formatTimestampToDateTime(updatedAt) : '--'}
        </span>
      ),
    },
    {
      title: t('components.button.action'),
      key: 'action',
      width: 100,
      align: 'left',
      render: (_: any, record: HandoverCarrierType) => (
        <Button type="link" onClick={() => showEditDrawer(record)} style={{ paddingLeft: 0 }}>
          {t('components.button.edit')}
        </Button>
      ),
    },
  ];

  if (loading && !pageResponse) return <Loading spinning={loading} />;

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      <FilterArea filters={tempFilters} setFilters={setTempFilters} />

      <div className="flex justify-end gap-2 mb-3">
        <Button
          type="primary"
          onClick={showNewDrawer}
          loading={saving}
          className="w-auto min-w-24"
        >
          <span className="font-bold">{t('components.button.new')}</span>
        </Button>
        <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.filter')}</span>
        </Button>
        <Button onClick={handleResetFilters} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.reset')}</span>
        </Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={pageResponse?.content ?? []}
        loading={loading || warehousesLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
        tableLayout="auto"
        className="mb-4"
      />

      <TablePaginationBar
        total={pageResponse?.totalElements ?? 0}
        current={currentPage}
        pageSize={pageSize}
        onChange={(page, size) => {
          setCurrentPage(page);
          if (size && size !== pageSize) {
            setPageSize(size);
            setCurrentPage(1);
          }
        }}
        onShowSizeChange={(_, size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        pageSizeOptions={['10', '20', '50', '100']}
      />

      <HandoverCarrierFormDrawer
        warehousesOptions={extendedWarehouseOptions}
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        onSubmit={handleSubmit}
        carrier={selectedCarrier}
        mode={drawerMode}
      />
    </div>
  );
};

const HandoverCarrierPage: React.FC = () => {
  return <PageBody />;
};

export default HandoverCarrierPage;
